import { useEffect } from "react";
import { getChangedFields } from "shared/helpers/unsavedChanges.store";

/**
 * Warn the user when refreshing, closing the tab or quitting the browser with unsaved
 * changes still pending (KP-2453).
 *
 * Browser-driven exits are left to the browser: `beforeunload` raises its own Leave / Cancel
 * dialog and the app adds nothing on top of it — no popup of ours. All we do besides that is
 * switch the highlighting on, so a user who chooses Cancel lands back on the page with the
 * unsaved fields already marked.
 *
 * The browser dialog cannot be customised and cannot offer "Save And Exit": saving is
 * asynchronous and would never finish before the page is torn down.
 */
const useBeforeUnloadGuard = (onDirtyUnload: () => void) => {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (getChangedFields().length === 0) return;

      event.preventDefault();
      // Chrome still requires returnValue to be assigned before it shows the dialog.
      event.returnValue = "";

      // Safe to run inline: it only paints the highlight. If the page really unloads the render
      // never happens; if the user cancels, it is already on screen when they land back.
      onDirtyUnload();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [onDirtyUnload]);
};

export default useBeforeUnloadGuard;
